import React, { useCallback, useMemo, useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Transition,
} from '@headlessui/react';
import { ChevronUpDownIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { skipToken } from '@reduxjs/toolkit/query';
import { useLocationContext } from '../../hooks/useLocationContext';
import { useScopedPath } from '../../components/ScopedLink';
import {
  useCreateStudentMutation,
  useGetClassesQuery,
  useGetClassTermsQuery,
  useListIntakeTemplatesQuery,
  useReactivateStudentMutation,
} from '../../store/api/localApi';
import Modal from '../../components/Modal';
import ClassDayPicker from '../../components/classes/ClassDayPicker';
import EffectiveFromField, {
  effectiveFromErrorMessage,
} from '../../components/classes/EffectiveFromField';
import { todayIso, formatTermOption, bookableTerms, coveringTerm } from '../../utils/terms';

/**
 * A LEFT child at this org whose name + DOB matches the one being added — the
 * server's returning-child guard (POSSIBLE_RETURNING_CHILD). The staff member
 * chooses to reactivate the existing record or deliberately create a new one.
 */
interface ReturningChildMatch {
  id: string;
  name: string;
  status: string;
  leftAt?: string;
  locationId: string;
  locationName: string;
}

const fieldClassName =
  'mt-1 block h-11 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500';

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface AddStudentModalProps {
  onClose: () => void;
}

interface ClassOption {
  id: string;
  name: string;
  isActive?: boolean;
}

/**
 * Add Student modal.
 *
 * Captures the bare minimum staff need at enrollment: optional child names,
 * the *required* parent email, and optional class/day assignments. Linking
 * the parent kicks off Cognito provisioning, location membership and the
 * REGISTRATION form invitation.
 *
 * Class selection follows a list-then-add pattern (cf. LinkedIn experience
 * sections): the user adds classes one at a time, each becomes a card with
 * its own inline day picker. Avoids the "which picker is which class?"
 * confusion of the previous checkboxes-then-pickers layout, and scales
 * cleanly to 10+ classes.
 */
const AddStudentModal: React.FC<AddStudentModalProps> = ({ onClose }) => {
  const { effectiveLocationId, activeLocationName } = useLocationContext();
  const navigate = useNavigate();
  const scopedPath = useScopedPath();
  const [createStudent, { isLoading }] = useCreateStudentMutation();
  const [reactivateStudent, { isLoading: isReactivating }] = useReactivateStudentMutation();
  const { data: classes = [] } = useGetClassesQuery(effectiveLocationId || '', {
    skip: !effectiveLocationId,
  });
  const {
    data: intakeTemplatesData,
    isLoading: intakeLoading,
    error: intakeError,
  } = useListIntakeTemplatesQuery(
    effectiveLocationId ? { locationId: effectiveLocationId } : skipToken
  );
  const intakeTemplates = intakeTemplatesData?.templates ?? [];
  const [checkedIntakeIds, setCheckedIntakeIds] = useState<string[]>([]);

  const toggleIntake = (formId: string) =>
    setCheckedIntakeIds((prev) =>
      prev.includes(formId) ? prev.filter((id) => id !== formId) : [...prev, formId]
    );

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    parentEmail: '',
  });
  // Populated when the server answers POSSIBLE_RETURNING_CHILD. While non-empty
  // the modal shows the returning-child prompt instead of the plain form.
  const [returningMatches, setReturningMatches] = useState<ReturningChildMatch[]>([]);
  // selectedClassIds is the source of truth; the new design adds/removes
  // entries from this list directly. daysByClass mirrors it so each card
  // can carry its own independent day selection.
  const [selectedClassIds, setSelectedClassIds] = useState<string[]>([]);
  const [daysByClass, setDaysByClass] = useState<Record<string, number[]>>({});
  // The term each selected class is being booked into. Populated by
  // ClassTermPicker once it resolves the default for its class (the term
  // covering today, or the nearest upcoming one when none does — see
  // ClassTermPicker below). Absent while that resolution is still pending, so
  // submit is gated on every selected class having a value here.
  const [termsByClass, setTermsByClass] = useState<Record<string, string>>({});
  // Each class's first day. Filled by its From field with that class's
  // earliest (today, or tomorrow once the class has started today), so submit is
  // gated on every selected class having one.
  const [fromByClass, setFromByClass] = useState<Record<string, string>>({});
  // The server's earliest for a class after a too-early refusal; that class's
  // From field floors at it.
  const [serverEarliestByClass, setServerEarliestByClass] = useState<Record<string, string>>({});
  const [error, setError] = useState('');

  const activeClasses = useMemo(() => classes.filter((cls) => cls.isActive), [classes]);
  const classById = useMemo(() => {
    const map = new Map<string, ClassOption>();
    activeClasses.forEach((cls) => map.set(cls.id, cls));
    return map;
  }, [activeClasses]);

  // Classes the user can still add — already-selected ones are hidden.
  const remainingClasses = useMemo(
    () => activeClasses.filter((cls) => !selectedClassIds.includes(cls.id)),
    [activeClasses, selectedClassIds]
  );

  const allSelectedHaveDays = selectedClassIds.every((id) => (daysByClass[id] || []).length > 0);
  // A class with a term already resolved (running today or the nearest
  // upcoming) is ready; one still resolving (query in flight) or with no
  // bookable term at all (neither running nor upcoming) blocks submit rather
  // than silently falling back to a 409 the admin can't see coming.
  const allSelectedHaveTerms = selectedClassIds.every((id) => !!termsByClass[id]);
  const allSelectedHaveFrom = selectedClassIds.every((id) => !!fromByClass[id]);
  const parentEmailValid = EMAIL_RX.test(form.parentEmail.trim());
  const canSubmit =
    !isLoading &&
    parentEmailValid &&
    (selectedClassIds.length === 0 ||
      (allSelectedHaveDays && allSelectedHaveTerms && allSelectedHaveFrom));

  const addClass = (classId: string) => {
    if (!classId || selectedClassIds.includes(classId)) return;
    setSelectedClassIds((prev) => [...prev, classId]);
    setDaysByClass((prev) => ({ ...prev, [classId]: [] }));
  };

  const removeClass = (classId: string) => {
    setSelectedClassIds((prev) => prev.filter((id) => id !== classId));
    setDaysByClass((prev) => {
      const next = { ...prev };
      delete next[classId];
      return next;
    });
    setTermsByClass((prev) => {
      const next = { ...prev };
      delete next[classId];
      return next;
    });
    clearFromForClass(classId);
  };

  /** Forget a class's From date so its field settles on a fresh default. */
  const clearFromForClass = (classId: string) => {
    setFromByClass((prev) => {
      const next = { ...prev };
      delete next[classId];
      return next;
    });
    setServerEarliestByClass((prev) => {
      const next = { ...prev };
      delete next[classId];
      return next;
    });
  };

  const setFromForClass = useCallback((classId: string, date: string) => {
    setFromByClass((prev) => (prev[classId] === date ? prev : { ...prev, [classId]: date }));
  }, []);

  const setDaysForClass = (classId: string, daysOfWeek: number[]) => {
    setDaysByClass((prev) => ({ ...prev, [classId]: daysOfWeek }));
  };

  const setTermForClass = (classId: string, termId: string | null) => {
    // The From date belongs to the term; a different one sets its own default.
    if (termsByClass[classId] && termsByClass[classId] !== termId) clearFromForClass(classId);
    setTermsByClass((prev) => {
      if (termId === null) {
        const next = { ...prev };
        delete next[classId];
        return next;
      }
      return { ...prev, [classId]: termId };
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  /**
   * Create the student. `force` is set only after the staff member has seen the
   * returning-child prompt and chosen "Create new anyway" — it tells the server
   * to skip the duplicate search and mint a fresh record.
   */
  const submitCreate = async (force: boolean) => {
    if (!effectiveLocationId) {
      setError('No location selected. Please select a location first.');
      return;
    }
    if (!parentEmailValid) {
      setError('Parent email is required.');
      return;
    }
    try {
      await createStudent({
        firstName: form.firstName,
        lastName: form.lastName,
        dateOfBirth: form.dateOfBirth || undefined,
        parentEmail: form.parentEmail,
        locationId: effectiveLocationId,
        classes: selectedClassIds.map((classId) => ({
          classId,
          daysOfWeek: daysByClass[classId] || [],
          // Sent whenever ClassTermPicker resolved one client-side, whether
          // it's the term running today or an upcoming one — a class between
          // terms has no "today" for the server to fall back on, which is
          // exactly the dead end this picker exists to avoid.
          ...(termsByClass[classId] ? { termId: termsByClass[classId] } : {}),
          ...(fromByClass[classId] ? { effectiveFrom: fromByClass[classId] } : {}),
        })),
        intakeFormIds: checkedIntakeIds.length > 0 ? checkedIntakeIds : undefined,
        ...(force ? { force: true } : {}),
      }).unwrap();
      onClose();
    } catch (err: any) {
      // A LEFT child at this org matches by name + DOB — surface the choice
      // rather than an error. Only meaningful when not already forcing.
      if (err?.data?.code === 'POSSIBLE_RETURNING_CHILD' && Array.isArray(err.data.matches)) {
        setReturningMatches(err.data.matches as ReturningChildMatch[]);
        setError('');
        return;
      }
      const dateProblem = effectiveFromErrorMessage(err?.data);
      if (dateProblem) {
        setError(dateProblem.message);
        const refusedClassId: string | undefined = err?.data?.classId;
        if (dateProblem.earliest && refusedClassId) {
          setServerEarliestByClass((prev) => ({
            ...prev,
            [refusedClassId]: dateProblem.earliest!,
          }));
          setFromForClass(refusedClassId, dateProblem.earliest);
        }
      } else if (err?.data?.error === 'CAPACITY_EXCEEDED') {
        setError(
          'One or more selected days no longer have capacity. Adjust your selection and try again.'
        );
      } else if (err?.data?.error === 'INVALID_ENROLLMENT_DAYS') {
        setError('Some selected days are outside the class schedule.');
      } else if (err?.data?.code === 'NO_RUNNING_TERM') {
        setError(
          'One of the selected classes has no term set up right now or coming up. Add a term for it before enrolling students.'
        );
      } else {
        setError(err?.data?.message || err?.data?.error || 'Failed to create student');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    await submitCreate(false);
  };

  /**
   * Re-enrol an existing LEFT record: reactivate it (status → ACTIVE /
   * PENDING_START, `leftAt` cleared) and take the staff member to that child's
   * page to book them into a term. Reactivation restores no enrolments by
   * design — enrolling is the deliberate next step, done on the child's page.
   */
  const handleReEnrol = async (match: ReturningChildMatch) => {
    setError('');
    try {
      await reactivateStudent(match.id).unwrap();
      onClose();
      navigate(scopedPath(`/students/${match.id}`));
    } catch (err: any) {
      setError(err?.data?.error || err?.data?.message || 'Failed to reactivate student');
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Add Student"
      maxWidth="lg"
      testId="add-student-modal"
    >
      {activeLocationName && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-800">
            Adding student to: <span className="font-medium">{activeLocationName}</span>
          </p>
        </div>
      )}
      {error && (
        <p className="text-sm text-red-600 mb-3" data-testid="add-student-error">
          {error}
        </p>
      )}
      {returningMatches.length > 0 ? (
        <div data-testid="returning-child-prompt" className="space-y-4">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
            <p className="text-sm text-amber-900">
              A child who previously left this organisation matches the name and date of birth you
              entered. Re-enrol the existing record so their history stays intact, or create a new
              record if this is a different child.
            </p>
          </div>
          <ul className="space-y-3">
            {returningMatches.map((match) => (
              <li
                key={match.id}
                data-testid={`dup-match-${match.id}`}
                className="rounded-md border border-gray-200 bg-white p-3"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{match.name}</p>
                    <p className="text-xs text-gray-500">
                      {match.locationName}
                      {match.leftAt ? ` · left ${match.leftAt}` : ''}
                    </p>
                  </div>
                  <button
                    type="button"
                    data-testid={`dup-match-reenrol-${match.id}`}
                    onClick={() => handleReEnrol(match)}
                    disabled={isReactivating}
                    className="shrink-0 px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                  >
                    {isReactivating
                      ? 'Re-enrolling...'
                      : `Re-enrol ${match.name}${match.leftAt ? ` (left ${match.leftAt})` : ''}`}
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              data-testid="dup-cancel"
              onClick={() => setReturningMatches([])}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Back
            </button>
            <button
              type="button"
              data-testid="dup-create-anyway"
              onClick={() => submitCreate(true)}
              disabled={isLoading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-700 hover:bg-gray-800 disabled:opacity-50"
            >
              {isLoading ? 'Creating...' : 'Create new anyway'}
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <fieldset>
            <legend className="text-sm font-medium text-gray-700 mb-2">Child</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600">First name</label>
                <input
                  data-testid="add-student-first-name"
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  placeholder="Optional"
                  className={fieldClassName}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600">Last name</label>
                <input
                  data-testid="add-student-last-name"
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  placeholder="Optional"
                  className={fieldClassName}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600">Date of birth</label>
                <input
                  data-testid="add-student-date-of-birth"
                  name="dateOfBirth"
                  type="date"
                  value={form.dateOfBirth}
                  onChange={handleChange}
                  className={fieldClassName}
                />
                <p className="mt-1 text-xs text-gray-400">
                  Helps spot a child returning to the service.
                </p>
              </div>
            </div>
          </fieldset>

          <fieldset>
            <legend className="text-sm font-medium text-gray-700 mb-2">
              Parent email <span className="text-red-500">*</span>
            </legend>
            <input
              data-testid="add-student-parent-email"
              name="parentEmail"
              type="email"
              value={form.parentEmail}
              onChange={handleChange}
              required
              placeholder="parent@example.com"
              className={fieldClassName}
            />
          </fieldset>

          <fieldset>
            <legend className="text-sm font-medium text-gray-700 mb-2">Classes</legend>
            {activeClasses.length === 0 ? (
              <p className="text-sm text-gray-500">No classes available for this location.</p>
            ) : (
              <div className="space-y-3">
                {/* List of configured class cards */}
                {selectedClassIds.length === 0 ? (
                  <p className="text-xs text-gray-500" data-testid="add-student-classes-empty">
                    No classes added yet. Use the picker below to add one.
                  </p>
                ) : (
                  selectedClassIds.map((classId) => {
                    const cls = classById.get(classId);
                    if (!cls) return null;
                    const days = daysByClass[classId] || [];
                    const needsDays = days.length === 0;
                    return (
                      <div
                        key={classId}
                        data-testid={`add-student-class-card-${classId}`}
                        className={`rounded-md border bg-white p-3 ${
                          needsDays ? 'border-amber-300' : 'border-gray-200'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="min-w-0">
                            <h4 className="text-sm font-semibold text-gray-900 truncate">
                              {cls.name}
                            </h4>
                            {needsDays && (
                              <p className="text-xs text-amber-700 mt-0.5">Pick at least one day</p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => removeClass(classId)}
                            aria-label={`Remove ${cls.name}`}
                            data-testid={`add-student-remove-class-${classId}`}
                            className="-mr-1 -mt-1 inline-flex items-center justify-center h-7 w-7 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                        <ClassTermPicker
                          classId={classId}
                          selectedTermId={termsByClass[classId] ?? null}
                          onChange={(termId) => setTermForClass(classId, termId)}
                          disabled={isLoading}
                        />
                        <ClassDayPicker
                          classId={classId}
                          className={cls.name}
                          value={days}
                          onChange={(d) => setDaysForClass(classId, d)}
                          disabled={isLoading}
                        />
                        {effectiveLocationId && termsByClass[classId] && (
                          <div className="mt-3">
                            <ClassFromField
                              classId={classId}
                              locationId={effectiveLocationId}
                              termId={termsByClass[classId]}
                              value={fromByClass[classId] ?? ''}
                              onChange={setFromForClass}
                              serverEarliest={serverEarliestByClass[classId]}
                              disabled={isLoading}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })
                )}

                {/* Add-a-class affordance — searchable combobox.
                  Hidden when every available class has been added. */}
                {remainingClasses.length > 0 && (
                  <ClassPicker classes={remainingClasses} onPick={addClass} disabled={isLoading} />
                )}
              </div>
            )}
          </fieldset>

          {/* Optional STUDENT_INTAKE templates — sent to the parent on enrollment.
            Hidden entirely when there are none and loading succeeded. */}
          {(intakeLoading || intakeError || intakeTemplates.length > 0) && (
            <fieldset data-testid="intake-forms-section">
              <legend className="text-sm font-medium text-gray-700 mb-2">
                Additional intake forms
              </legend>
              {intakeLoading ? (
                <p data-testid="intake-forms-loading" className="text-xs text-gray-500">
                  Loading intake forms…
                </p>
              ) : intakeError ? (
                <p data-testid="intake-forms-error" className="text-xs text-red-600">
                  Could not load intake forms. They can be sent later from the Forms page.
                </p>
              ) : (
                <div className="space-y-2">
                  {intakeTemplates.map((t) => (
                    <label key={t.formId} className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        data-testid={`intake-form-checkbox-${t.formId}`}
                        checked={checkedIntakeIds.includes(t.formId)}
                        onChange={() => toggleIntake(t.formId)}
                        disabled={isLoading}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>
                        <span className="block text-sm text-gray-900">{t.name}</span>
                        {t.description && (
                          <span className="block text-xs text-gray-500">{t.description}</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </fieldset>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="add-student-submit"
              disabled={!canSubmit}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              {isLoading ? 'Creating...' : 'Add Student'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};

/**
 * Searchable class picker. Renders as a "+ Add a class" button until
 * focused, then expands into a Headless UI Combobox with type-to-filter.
 */
const ClassPicker: React.FC<{
  classes: ClassOption[];
  onPick: (classId: string) => void;
  disabled?: boolean;
}> = ({ classes, onPick, disabled }) => {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return classes;
    return classes.filter((cls) => cls.name.toLowerCase().includes(q));
  }, [classes, query]);

  return (
    <Combobox
      value={null}
      onChange={(value: ClassOption | null) => {
        if (value) {
          onPick(value.id);
          setQuery('');
        }
      }}
      disabled={disabled}
      immediate
    >
      <div className="relative">
        <div className="relative w-full cursor-default rounded-md border border-dashed border-gray-300 bg-white text-left focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
          <ComboboxButton
            as="div"
            className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700"
          >
            <PlusIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
            <ComboboxInput
              data-testid="add-student-class-picker-input"
              className="flex-1 bg-transparent border-0 p-0 text-sm text-gray-900 placeholder:text-gray-500 focus:ring-0 focus:outline-none"
              placeholder="Add a class…"
              displayValue={() => ''}
              onChange={(e) => setQuery(e.target.value)}
            />
            <ChevronUpDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
          </ComboboxButton>
        </div>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => setQuery('')}
        >
          <ComboboxOptions
            data-testid="add-student-class-picker-options"
            className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none"
          >
            {filtered.length === 0 ? (
              <div className="cursor-default select-none px-3 py-2 text-sm text-gray-500">
                {query ? `No class matches "${query}"` : 'No more classes to add'}
              </div>
            ) : (
              filtered.map((cls) => (
                <ComboboxOption
                  key={cls.id}
                  value={cls}
                  data-testid={`add-student-class-picker-option-${cls.id}`}
                  className={({ active }) =>
                    `relative cursor-pointer select-none px-3 py-2 ${
                      active ? 'bg-primary-50 text-primary-900' : 'text-gray-900'
                    }`
                  }
                >
                  {cls.name}
                </ComboboxOption>
              ))
            )}
          </ComboboxOptions>
        </Transition>
      </div>
    </Combobox>
  );
};

/**
 * Term picker for one class card. Mirrors EnrollModal.tsx's term-picker logic
 * exactly — this is the flow the admin-reported bug lived in: registering a
 * new child into a class between terms (current term ended, next one not
 * started) silently defaulted to "the term covering today", which is null in
 * that gap, and the whole batch 409'd with NO_RUNNING_TERM before a single
 * student row was written. There was no picker at all to see or change which
 * term was meant.
 *
 * Reports its resolved default up via onChange as soon as the terms query
 * settles (including reporting `null` when there is no bookable term at all,
 * so the parent's "every selected class has a term" gate can block submit
 * instead of silently omitting termId and hitting the same 409).
 */
const ClassTermPicker: React.FC<{
  classId: string;
  selectedTermId: string | null;
  onChange: (termId: string | null) => void;
  disabled?: boolean;
}> = ({ classId, selectedTermId, onChange, disabled }) => {
  const { data: terms } = useGetClassTermsQuery(classId);
  const today = todayIso();
  // Only the term running now and any still to come can be booked into.
  const options = useMemo(() => bookableTerms(terms ?? [], today), [terms, today]);
  const covering = coveringTerm(options, today);
  const defaultTermId = covering?.termId ?? options[0]?.termId ?? null;
  // The picker earns its place whenever there is a real choice OR the only
  // option is not the term running today — e.g. a class between terms, where
  // the sole bookable term is an upcoming one.
  const showPicker = options.length > 1 || (options.length === 1 && !covering);

  const hasReportedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!terms) return; // still loading — nothing to report yet
    if (hasReportedRef.current === defaultTermId) return;
    hasReportedRef.current = defaultTermId;
    onChange(defaultTermId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terms, defaultTermId]);

  if (!terms) {
    return <p className="mt-2 text-xs text-gray-400">Loading terms…</p>;
  }
  if (options.length === 0) {
    return (
      <p className="mt-2 text-xs text-amber-700" data-testid={`add-student-no-term-${classId}`}>
        This class has no term set up right now or coming up. Add a term for it before enrolling
        students.
      </p>
    );
  }
  if (!showPicker) {
    return null;
  }

  return (
    <div className="mt-2 mb-3">
      <label
        htmlFor={`add-student-term-select-${classId}`}
        className="block text-xs font-medium text-gray-600 mb-1"
      >
        Term
      </label>
      <select
        id={`add-student-term-select-${classId}`}
        data-testid={`add-student-term-select-${classId}`}
        value={selectedTermId ?? defaultTermId ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="block w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-primary-500 focus:ring-primary-500"
      >
        {options.map((term) => (
          <option key={term.termId} value={term.termId}>
            {formatTermOption(term, today)}
          </option>
        ))}
      </select>
    </div>
  );
};

/**
 * The From date for one class card: when this child's enrolment in the class
 * starts. Floors at the chosen term's start (a forward booking starts when
 * its term does) and at any earliest the server sent back; tops out at the
 * term's last day. The session rule itself lives in EffectiveFromField.
 */
const ClassFromField: React.FC<{
  classId: string;
  locationId: string;
  termId: string;
  value: string;
  onChange: (classId: string, date: string) => void;
  serverEarliest?: string;
  disabled?: boolean;
}> = ({ classId, locationId, termId, value, onChange, serverEarliest, disabled }) => {
  const { data: terms } = useGetClassTermsQuery(classId);
  const term = terms?.find((t) => t.termId === termId);
  const floors = [term?.startDate, serverEarliest].filter((d): d is string => !!d).sort();
  const handleChange = useCallback((date: string) => onChange(classId, date), [onChange, classId]);
  return (
    <EffectiveFromField
      testId={`add-student-from-${classId}`}
      classId={classId}
      locationId={locationId}
      value={value}
      onChange={handleChange}
      notBefore={floors[floors.length - 1]}
      max={term?.endDate}
      disabled={disabled}
    />
  );
};

export default AddStudentModal;
